import React from 'react';
import {
  ConsumptionEmissionDataType,
  convertArrayString,
  oneCMSVtpConfiguration,
  useConsumptionEmissionData,
  useServicesContext,
  VehicleBasic,
} from '@oneaudi/vtp-shared';
import { CEContainer, ResponsiveLayoutContainer } from './ConsumptionEmissionLayerPlp.styles';
import { TestCycleTypesFromConfig } from '@oneaudi/vtp-configuration-service';
import { ContentService } from '@oneaudi/content-service';
import { Headline } from '@oneaudi/unified-web-components';
import { SYS_SPACE_RELATIVE_LG } from '@oneaudi/unified-web-common';
import { useI18n } from '@oneaudi/i18n-context';
import { EfficiencyDisplay } from './EfficiencyDisplay';
import { ConsumptionAndEmissionDisplay } from './ConsumptionAndEmissionDisplay';

interface ConsumptionEmissionLayerPlpProps {
  vehicle: VehicleBasic;
}

export const ConsumptionEmissionLayerPlp: React.FC<ConsumptionEmissionLayerPlpProps> = ({
  vehicle,
}) => {
  const audiContentService = useServicesContext().getAdditionalService(
    'audi-content-service'
  ) as ContentService;
  const content = audiContentService.getContent();
  const vtpConfig: oneCMSVtpConfiguration = content?.fields?.vtpConfiguration?.fields;
  const {
    consumptionEmission_consumptionEmissionRepresentation: consumptionEmissionRepresentation,
    consumptionEmission_scsConsumptionEmissionBlacklist: scsConsumptionEmissionBlacklist,
    consumptionEmission_tilesEmissionRepresentation: tilesEmissionRepresentation,
    consumptionEmission_wltpLink: wltpLink,
    consumptionEmission_nedcLink: nedcLink,
    useEfficiencyImage,
    urls_eecImageUrl: eecImgURL,
  } = vtpConfig || {};
  const cEDataConfig = {
    consumptionEmissionRepresentation,
    scsConsumptionEmissionBlacklist,
    tilesEmissionRepresentation,
    wltpLink,
    nedcLink,
  };
  const ceData = { consumptionEmission: { ...cEDataConfig }, useEfficiencyImage, eecImgURL };
  const consumptionBlacklist = ceData.consumptionEmission?.scsConsumptionEmissionBlacklist || '[]';
  const showBothTestCycles =
    ceData.consumptionEmission?.consumptionEmissionRepresentation?.includes(
      TestCycleTypesFromConfig.NEDC
    ) &&
    ceData.consumptionEmission?.consumptionEmissionRepresentation?.includes(
      TestCycleTypesFromConfig.WLTP
    );
  const consumptionBlacklistArray: string[] = convertArrayString(consumptionBlacklist);
  const data = {
    wltp: useConsumptionEmissionData(
      vehicle,
      TestCycleTypesFromConfig.WLTP,
      consumptionBlacklistArray,
      ceData.consumptionEmission
    ),
    nedc: useConsumptionEmissionData(
      vehicle,
      TestCycleTypesFromConfig.NEDC,
      consumptionBlacklistArray,
      ceData.consumptionEmission
    ),
  };

  const [
    primaryTestCycle = TestCycleTypesFromConfig.WLTP,
    secondaryTestCycle = TestCycleTypesFromConfig.NEDC,
  ] = ceData.consumptionEmission?.consumptionEmissionRepresentation as TestCycleTypesFromConfig[];
  const primaryTestCycleHasData = !!data[primaryTestCycle]?.fuelData?.length;
  const secondaryTestCycleHasData = !!data[secondaryTestCycle]?.fuelData?.length;

  // eslint-disable-next-line no-nested-ternary
  const firstConsumptionEmissionMainBlockData = primaryTestCycleHasData
    ? data[primaryTestCycle]
    : secondaryTestCycleHasData
      ? data[secondaryTestCycle]
      : null;

  const secondConsumptionEmissionMainBlockData =
    showBothTestCycles && primaryTestCycleHasData && secondaryTestCycleHasData
      ? data[secondaryTestCycle]
      : null;

  const consumptionEmissionData: (ConsumptionEmissionDataType | null)[] =
    [firstConsumptionEmissionMainBlockData, secondConsumptionEmissionMainBlockData].filter(
      Boolean
    ) ?? [];
  const WLTPData = consumptionEmissionData.filter((CEData) => CEData?.testCycleType === 'wltp');
  const dataToRender = WLTPData.length > 0 ? WLTPData : consumptionEmissionData;
  return (
    <CEContainer>
      <Headline
        tag="h3"
        variant={{
          order: '3',
          type: 'headline',
          weight: 'normal',
        }}
        margin={[0, 0, SYS_SPACE_RELATIVE_LG, 0]}
      >
        {useI18n({
          defaultMessage: 'Consumption and emission',
          id: 'nemo.ui.sc.consumption.headline',
        })}
      </Headline>
      <ResponsiveLayoutContainer>
        <ConsumptionAndEmissionDisplay dataToRender={dataToRender} />
        <EfficiencyDisplay
          vehicle={vehicle}
          useEfficiencyImage={useEfficiencyImage}
          eecImageUrl={eecImgURL}
        />
      </ResponsiveLayoutContainer>
    </CEContainer>
  );
};
